export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>

  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

let originalFetch: typeof fetch | null = null

/**
 * Installs a global fetch interceptor in the browser window to ensure all `/api`
 * calls automatically inherit CSRF headers, credentials, and 401 handling.
 */
export function installFetchInterceptor(): void {
  if (typeof window === 'undefined' || originalFetch) return
  originalFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const urlStr = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
    if (urlStr && (urlStr.startsWith('/api') || urlStr.includes('/api/'))) {
      return apiFetch(input, init)
    }
    return originalFetch!(input, init)
  }
}

/**
 * Standard fetch wrapper that automatically attaches CSRF header (X-Requested-With: erp),
 * includes session cookies, and handles global 401 unauthenticated redirect.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  let headers: Record<string, string> = {}
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((v, k) => {
        headers[k] = v
      })
    } else if (Array.isArray(init.headers)) {
      for (const [k, v] of init.headers) {
        headers[k] = v
      }
    } else {
      headers = { ...(init.headers as Record<string, string>) }
    }
  }
  if (!headers['X-Requested-With'] && !headers['x-requested-with']) {
    headers['X-Requested-With'] = 'erp'
  }

  const fetcher = originalFetch ?? fetch
  const res = await fetcher(input, {
    ...init,
    headers,
    credentials: init?.credentials ?? 'same-origin',
  })

  // Global 401 unauthenticated handling (redirects browser if in window context)
  if (res.status === 401 && typeof window !== 'undefined') {
    const currentPath = window.location.pathname
    if (currentPath !== '/login') {
      window.location.href = `/login?from=${encodeURIComponent(currentPath + window.location.search)}`
    }
  }

  return res
}
